// MonitorLocationChange.jsx
import React from 'react';
import PropTypes from 'prop-types';

import withRouter from '../component/WithRouter.jsx';
import { EventTypes, getDataFromPath } from '../resource/mixpanel.js';
import { trackPageView } from '../resource/customerService.js';

/**
 * @typedef {Object} Props
 * @property {import("react-router").match} match
 * @property {import("history").Location} location
 *
 * @extends {React.PureComponent<Props>}
 */
export class MonitorLocationChange extends React.PureComponent {
  openedTime = 0;
  closedTime = 0;
  intercomUpdateTimer = null;
  componentDidMount() {
    const { location, route, pushRouteHistory } = this.props;
    this.trackLocationChange({
      pathname: location.pathname,
    });
    if (route) {
      setTimeout(() => pushRouteHistory({ route }), 0);
    }
  }

  /**
   * @param {Props} prevProps
   */
  componentDidUpdate(prevProps) {
    const {
      location,
      route,
      pushRouteHistory,
      cancelShopping,
      shopId,
      isDiamondShopOpen,
    } = this.props;
    const { location: prevLocation, route: prevRoute } = prevProps;
    if (location.pathname !== prevLocation.pathname) {
      prevLocation.pathname &&
        this.trackLocationChange({
          pathname: prevLocation.pathname,
          isViewOpenedEvent: false,
        });
      this.trackLocationChange({
        pathname: location.pathname,
      });

      this.intercomUpdate();
    }
    if (location.pathname !== prevLocation.pathname && isDiamondShopOpen) {
      cancelShopping({ shopId });
    }
    if (route && route !== prevRoute) {
      setTimeout(() => pushRouteHistory({ route }), 0);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.intercomUpdateTimer);
  }

  render() {
    return null;
  }

  trackLocationChange({ pathname = '', isViewOpenedEvent = true }) {
    const {
      userEventReportUrl,
      timestampOffsetMilliseconds,
      sendViewTracking,
    } = this.props;
    const payload = getDataFromPath({
      pathname,
    });

    if (pathname === '/user-event') {
      payload.extraProps = {
        'campaign.url': userEventReportUrl,
      };
    }

    if (isViewOpenedEvent) {
      this.openedTime = Date.now() + timestampOffsetMilliseconds;
    } else {
      this.closedTime = Date.now() + timestampOffsetMilliseconds;
      payload.extraProps = {
        $duration: (this.closedTime - this.openedTime) / 1000,
      };
      payload.shouldSendBeacon = true;
    }

    sendViewTracking({
      type: isViewOpenedEvent ? EventTypes.VIEW_OPENED : EventTypes.VIEW_CLOSED,
      payload,
    });
  }

  intercomUpdate() {
    // the debounce logic here should move to `resource/customerService.js`
    clearTimeout(this.intercomUpdateTimer);
    const { intercomUpdateDebounceMsec } = this.props;
    this.intercomUpdateTimer = setTimeout(() => {
      trackPageView();
    }, intercomUpdateDebounceMsec);
  }
}

MonitorLocationChange.propTypes = {
  location: PropTypes.object.isRequired,
  route: PropTypes.string,
  pushRouteHistory: PropTypes.func,
  cancelShopping: PropTypes.func,
  sendViewTracking: PropTypes.func,
  shopId: PropTypes.string,
  userEventReportUrl: PropTypes.string,
  timestampOffsetMilliseconds: PropTypes.number,
  intercomUpdateDebounceMsec: PropTypes.number,
  isDiamondShopOpen: PropTypes.bool,
};

MonitorLocationChange.defaultProps = {
  route: '',
  pushRouteHistory: () => null,
  cancelShopping: () => null,
  sendViewTracking: () => null,
  shopId: '',
  userEventReportUrl: '',
  timestampOffsetMilliseconds: 0,
  intercomUpdateDebounceMsec: 5000,
  isDiamondShopOpen: false,
};

export default withRouter(MonitorLocationChange);
